import { DecimalPipe, DatePipe } from "@angular/common";
import { Component, OnInit, AfterViewInit, AfterViewChecked, OnDestroy, AfterContentInit, AfterContentChecked, Inject, ChangeDetectorRef } from "@angular/core";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Subscription, Subject } from "rxjs";
import { CostConfigDto, MachineIdDto, MachineV2Dto, EventV2Dto, FieldV2Dto, FieldIdDto, LocationV2Dto, LocationIdDto, EnterpriseCustomerV2Dto, EnterpriseCustomerIdDto, UserDto, UserIdDto, EnterpriseDto, EnterpriseIdDto, ImplementV2Dto, ImplementIdDto, ExaAdapterV2Dto, ExaAdapterIdDto, ExaBeaconV2Dto, ExaBeaconIdDto, GeometryIdDto, EmployeeV2Dto, EmployeeIdDto, AccountingConfigDto } from "src/app/api/exa";
import { CostPerEnum } from "src/app/enums/cost-per-enum";
import { CurrencyEnum } from "src/app/enums/currency-enum";
import { EventTypeEnum } from "src/app/enums/event-type-enum";
import { GNSSSourceEnum } from "src/app/enums/gnss-source-enum";
import { MachineTypeEnum } from "src/app/enums/machine-type-enum";
import { WorkstateEnum } from "src/app/enums/workstate-enum";
import { HelperFunctions } from "src/app/helper/helper-functions";
import { LogLevelEnum, LogService } from "src/app/services/log.service";
import { NameFormattingService } from "src/app/services/name-formatting.service";


@Component({
  template: '',
})
export abstract class BaseDataComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy, AfterContentInit, AfterContentChecked
{
  protected logging = true;
  private lifeCycleLog = true;
  private lifeCycleLogView = true;
  private lifeCycleLogViewCheck = false;
  private lifeCycleLogContent = false;
  private lifeCycleLogContentCheck = false;

  private loadingMap: Map<string, boolean> = new Map();

  private language = 'de';
  public pipeTranslate: TranslatePipe | undefined;
  private pipeDecimal: DecimalPipe | undefined;
  private pipeDate: DatePipe | undefined;

  private subscriptions: Map<string, Subscription> = new Map();
  protected destroy$: Subject<void> = new Subject();
  private _unsubscribeInternal$: Subject<void> = new Subject(); //internal unsubscribe

  private accountingTranslationMap: Map<string, string> = new Map();

  constructor(
    @Inject(String) private __className: string,
    public __logService: LogService,
    public __translateService: TranslateService,
    public __changeDetectorRef: ChangeDetectorRef,
    // public __appContext: AppContextProvider
  ) {
    if (!this.__className) {
      this.__className = 'BaseDataComponent';
    }
    if (this.logging && this.lifeCycleLog) {
      this.log(LogLevelEnum.DEBUG, 'construct');
    }
    this.initPipes();
  }

  protected init() {
    // extending classes have to implement this
  }

  protected afterViewInit() {
    // extending classes have to implement this
  }

  protected afterViewChecked() {
    // extending classes have to implement this
  }

  protected afterContentInit() {
    // extending classes have to implement this
  }

  protected afterContentChecked() {
    // extending classes have to implement this
  }

  protected onDestroy() {
    // extending classes have to implement this
  }

  ngOnInit(): void {
    if (this.logging && this.lifeCycleLog) {
      this.log(LogLevelEnum.DEBUG, 'ngOnInit');
    }
    this.init();
  }

  ngAfterViewInit(): void {
    if (this.logging && this.lifeCycleLog && this.lifeCycleLogView) {
      this.log(LogLevelEnum.DEBUG, 'ngAfterViewInit');
    }
    this.afterViewInit();
  }

  ngAfterViewChecked(): void {
    if (this.logging && this.lifeCycleLog && this.lifeCycleLogViewCheck) {
      this.log(LogLevelEnum.DEBUG, 'ngAfterViewChecked');
    }
    this.afterViewChecked();
  }

  ngAfterContentInit(): void {
    if (this.logging && this.lifeCycleLog && this.lifeCycleLogContent) {
      this.log(LogLevelEnum.DEBUG, 'ngAfterContentInit');
    }
    this.afterContentInit();
  }

  ngAfterContentChecked(): void {
    if (this.logging && this.lifeCycleLog && this.lifeCycleLogContentCheck) {
      this.log(LogLevelEnum.DEBUG, 'ngAfterContentChecked');
    }
    this.afterContentChecked();
  }

  private initPipes() {
    this.pipeTranslate = new TranslatePipe(this.__translateService, this.__changeDetectorRef);
    // set currrent language
    this.changeLanguage(this.__translateService.currentLang);
  }

  protected changeLanguage(language: string) {
    this.language = language;
    if (this.logging) {
      this.log(LogLevelEnum.DEBUG, 'Language to use for pipes: ' + this.language);
    }
    // init pipes
    this.pipeDecimal = new DecimalPipe(this.language);
    this.pipeDate = new DatePipe(this.language);
  }

  public formatDate(date: string | number | Date | undefined, format?: string): string {
    if (date == null || date == undefined || !this.pipeDate) {
      return '';
    }
    const x = this.pipeDate.transform(date, format, undefined, this.language);
    return x ? x : '';
  }

  public formatDecimal(decimal: number | undefined, format?: string): string {    
    if (decimal == null || decimal == undefined || !this.pipeDecimal) {
      return '';
    }
    const x = this.pipeDecimal.transform(decimal, format, this.language);
    return x ? x : '';
  }

  public roundDecimal(value: number, decimals?: number): number {
    if (decimals == null || decimals == undefined) {
      decimals = 0;
    }
    return HelperFunctions.round(value, decimals);
  }

  public getTranslation(translatable: string): string {
    if (!this.pipeTranslate) {
      return '';
    }
    return this.pipeTranslate.transform(translatable) as string;
  }

  ngOnDestroy(): void {
    if (this.logging && this.lifeCycleLog) {
      this.log(LogLevelEnum.DEBUG, 'ngOnDestroy');
    }
    for (const keyValue of this.subscriptions) {
      keyValue[1].unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
    this._unsubscribeInternal$.next();
    this._unsubscribeInternal$.complete();
    this.onDestroy();
  }

  protected stopOnViewDestroy(): Subject<void> {
    return this._unsubscribeInternal$;
  }

  protected addSubscription(key: string, subscription: Subscription) {
    if (this.subscriptions.has(key)) {
      this.subscriptions.get(key)?.unsubscribe();
    }
    this.subscriptions.set(key, subscription);
  }

  protected setLoading(loading: boolean, subcomponentKey?: string) {
    if (!subcomponentKey) {
      subcomponentKey = 'main';
    }
    this.loadingMap.set(subcomponentKey, loading);
    //this.log(LogLevelEnum.DEBUG, 'setLoading(' + loading + ',' + subcomponentKey + ')');
    //this.__changeDetectorRef.detectChanges();
  }

  public isLoading(subcomponentKey?: string): boolean {
    if (!subcomponentKey) {
      subcomponentKey = 'main';
    }
    if (this.loadingMap.has(subcomponentKey)) {
      const loading = this.loadingMap.get(subcomponentKey);
      if (loading != undefined && loading != null) {
        return loading;
      }
    }
    //this.log(LogLevelEnum.DEBUG, 'isLoading('+ subcomponentKey + ')', loading);
    return false;
  }

  protected log(logLevel: LogLevelEnum, message?: string, payload?: unknown) {
    if (message) {
      message = this.__className + ': ' + message;
    } else {
      message = this.__className;
    }
    switch (logLevel) {
      case LogLevelEnum.ERROR:
        this.__logService.error(message, payload);
        break;
      case LogLevelEnum.WARN:
        this.__logService.warning(message, payload);
        break;
      case LogLevelEnum.INFO:
        this.__logService.info(message, payload);
        break;
      case LogLevelEnum.DEBUG:
        this.__logService.debug(message, payload);
        break;
    }
  }

  // public getCurrency(): string {
  //   if (this.__appContext.currentEnterpriseConfig && this.__appContext.currentEnterpriseConfig.currency) {
  //     return this.currencyToChar(this.__appContext.currentEnterpriseConfig.currency);
  //   } else {
  //     return '-';
  //   }
  // }

  public currencyToChar(currency: string): string {
    switch (currency) {
      case CurrencyEnum.ARS:
      case CurrencyEnum.AUS:
      case CurrencyEnum.CAD:
      case CurrencyEnum.COP:
      case CurrencyEnum.MXN:
      case CurrencyEnum.NZD:
      case CurrencyEnum.USD:
      case CurrencyEnum.UYU:
        return '$';
      case CurrencyEnum.GBP:
        return '£';
      case CurrencyEnum.JPY:
        return '¥';
      case CurrencyEnum.CHF:
        return '₣';
      case CurrencyEnum.EUR:
        return '€';
      case CurrencyEnum.UAH:
        return '₴';
      case CurrencyEnum.INR:
        return '₹';
      case CurrencyEnum.RUB:
        return '₽';
      case CurrencyEnum.BYN:
        return 'Br';
      case CurrencyEnum.RSD:
        return 'Din';
      case CurrencyEnum.HUF:
        return 'Ft';
      case CurrencyEnum.DKK:
      case CurrencyEnum.NOK:
      case CurrencyEnum.SEK:
        return 'Kr';
      case CurrencyEnum.RON:
        return 'L';
      case CurrencyEnum.ZAR:
        return 'R';
      case CurrencyEnum.BRL:
        return 'R$';
      case CurrencyEnum.PLN:
        return 'zł';
      case CurrencyEnum.MKD:
        return 'ден';
      case CurrencyEnum.BAM:
        return 'KM';
      case CurrencyEnum.BGN:
        return 'лв';
      default:
        return '-';
    }
  }

  public getIconForCostConfig(costConfig?: CostConfigDto): string {
    if (costConfig && costConfig.costPer) {
      switch (costConfig.costPer) {
        case CostPerEnum.H:
          return 'timer';
        case CostPerEnum.HA:
          return 'apps';
        case CostPerEnum.KM:
          return 'map';
        case CostPerEnum.L:
          return 'local_gas_station';
        default:
          return 'timer';
      }
    }
    return 'timer';
  }

  public getMachineIconName(machine: MachineIdDto | MachineV2Dto | undefined): string {
    if (!machine) {
      return 'tractor';
    }
    switch (machine.type) {
      case MachineTypeEnum.TRACTOR:
        if (machine.make) {
          if (
            machine.make.toUpperCase() === 'DaimlerChrysler'.toUpperCase() ||
            machine.make.toUpperCase() === 'Mercedes-Benz'.toUpperCase()
          ) {
            return 'unimog';
          } else if (machine.make.toUpperCase() === 'AgXeed'.toUpperCase()) {
            return 'robot';
          }
        }
        return 'tractor';
      case MachineTypeEnum.COMBINE:
        return 'combine';
      case MachineTypeEnum.FORAGE_HARVESTER:
        return 'harvester';
      case MachineTypeEnum.TRUCK:
        return 'truck';
      case MachineTypeEnum.SYREN:
        return 'tractor';
      case MachineTypeEnum.EXCAVATOR:
        return 'excavator';
      case MachineTypeEnum.WHEELLOADER:
        return 'wheelloader';
      case MachineTypeEnum.TELEHANDLER:
        return 'telehandler';
      case MachineTypeEnum.SELF_PROPELLED:
        return 'self_propelled';
      default:
        return 'tractor';
    }
  }

  public getEventIcon(event?: EventV2Dto): string {
    if (event && event.type) {
      switch (event.type) {
        case EventTypeEnum.REFUELING:
          return 'local_gas_station';
        default:
          return 'info';
      }
    }
    return 'info';
  }

  private getColorBase(color: string | undefined, defaultColor = '#000000', opacity?: number): string {
    // check if color is of format #RRGGBB[AA]
    const hexColorTest = new RegExp('^#?(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})\\b');
    // check default color just to be sure
    if (!hexColorTest.test(defaultColor)) {
      this.__logService.warning('default color is invalid:', defaultColor);
      defaultColor = '#000000';
    }
    let alphaHex = '';
    if (opacity != undefined || opacity != null) {
      if (opacity < 0) {
        opacity = 0;
      } else if (opacity > 1) {
        opacity = 1;
      }
      alphaHex = this.alphaToHex(opacity);
    }
    if (!color) {
      color = defaultColor;
    } else {
      // check color, if fails use default color as fallback
      if (!hexColorTest.test(color)) {
        // fail -> no color
        color = defaultColor;
      }
    }
    // strip off the alpha channel if available from previously stored 'old' data
    // will be ignored since we might override it by some other alpha/opacity value
    // 000000AA -> 8
    // #000000AA -> 9
    if (color.length == 8 || color.length == 9) {
      color = color.substring(0, color.length - 2);
    }
    if (alphaHex) {
      color = color + alphaHex;
    }
    if (!color.startsWith('#')) {
      color = '#' + color;
    }
    return color.toLowerCase();
  }

  private alphaToHex(alpha: number) {
    if (alpha < 0) {
      alpha = 0;
    } else if (alpha > 1) {
      alpha = 1;
    }
    // Calculate the equivalent alpha value in the range of 0 to 255
    const alphaInt = Math.round(alpha * 255);
    // Convert alphaInt to hexadecimal string
    const alphaHex = alphaInt.toString(16).toLowerCase();
    // Pad the hexadecimal value with leading zero if needed
    const paddedAlphaHex = alphaHex.padStart(2, '0');
    return paddedAlphaHex;
  }

  public getColor(
    item?:
      | MachineV2Dto
      | MachineIdDto
      | FieldV2Dto
      | FieldIdDto
      | LocationV2Dto
      | LocationIdDto
      | EnterpriseCustomerV2Dto
      | EnterpriseCustomerIdDto,
    defaultColor = '#000000',
    opacity?: number
  ): string {
    return this.getColorBase(item?.color, defaultColor, opacity);
  }

  public getColorWorkstate(item?: string | WorkstateEnum): string {
    switch (item) {
      case WorkstateEnum.WORKING:
        return '#57b41a';
      case WorkstateEnum.TURNING:
        return '#006cff';
      case WorkstateEnum.ROAD:
        return '#ffff00';
      case WorkstateEnum.PREPARATION:
        return '#8b00ff';
      case WorkstateEnum.MISC_FIELD:
        return '#b41a1a';
      case WorkstateEnum.MISC_ROAD:
        return '#ff7400';
      case WorkstateEnum.TRANSPORT_LOADING:
        return '#006cff';
      case WorkstateEnum.TRANSPORT_UNLOADING:
        return '#4ebcff';
      case WorkstateEnum.TRANSPORT_UNLOADED:
        return '#95ff15';
      case WorkstateEnum.TRANSPORT_LOADED:
        return '#57b41a';
      case WorkstateEnum.DRIVING:
        return '#ffff00';
      case WorkstateEnum.DRIVING_MISC:
        return '#ff7400';
      case WorkstateEnum.MISC_FIELD_GRAINTANK_FULL:
        return '#ff000d';
      case WorkstateEnum.THRESHING:
        return '#57b41a';
      case WorkstateEnum.THRESHING_WITH_STRAWCHOPPER:
        return '#115900';
      case WorkstateEnum.THRESHING_UNLOADING:
        return '#95ff15';
      case WorkstateEnum.THRESHING_WITH_STRAWCHOPPER_UNLOADING:
        return '#298221';
      case WorkstateEnum.TRANSPORT_UNLOADING_DRIVING:
        return '#4ebcff';
      default:
        return '#000000';
    }
  }

  public getColorGNSSSource(item?: string | GNSSSourceEnum): string {
    switch (item) {
      case GNSSSourceEnum.AUTO:
        return '#27a89a';
      case GNSSSourceEnum.GNSS_INTERN:
        return 'aqua';
      case GNSSSourceEnum.GNSS_MACHINE:
        return 'fuchsia';
      default:
        return '#000000';
    }
  }

  public getColorMisc(key?: string): string {
    switch (key) {
      case 'WAITING':
        return '#ff7400';
      case 'IDLE':
        return '#8b00ff';
      default:
        return '#000000';
    }
  }

  public getUserName(user?: UserDto | UserIdDto): string {
    return NameFormattingService.getUserName(user);
  }

  public getEnterpriseName(enterprise?: EnterpriseDto | EnterpriseIdDto): string {
    return NameFormattingService.getEnterpriseName(enterprise);
  }

  public getMachineName(machine?: MachineV2Dto | MachineIdDto, noMakeModelFillOnEmpty?: boolean): string {
    return NameFormattingService.getMachineName(machine, noMakeModelFillOnEmpty);
  }

  public getImplementName(implement?: ImplementV2Dto | ImplementIdDto, noMakeModelFillOnEmpty?: boolean): string {
    return NameFormattingService.getImplementName(implement, noMakeModelFillOnEmpty);
  }

  public getAdapterName(adapter?: ExaAdapterV2Dto | ExaAdapterIdDto): string {
    return NameFormattingService.getExaAdapterName(adapter);
  }

  public getBeaconName(beacon?: ExaBeaconV2Dto | ExaBeaconIdDto): string {
    return NameFormattingService.getExaBeaconName(beacon);
  }

  public getFieldName(field?: FieldV2Dto | FieldIdDto): string {
    return NameFormattingService.getFieldName(field);
  }

  public getLocationName(location?: LocationV2Dto | LocationIdDto): string {
    return NameFormattingService.getLocationName(location);
  }

  public getGeometryName(geometry?: GeometryIdDto): string {
    return NameFormattingService.getGeometryName(geometry);
  }

  public getEmployeeName(employeeDto?: EmployeeV2Dto | EmployeeIdDto): string {
    return NameFormattingService.getEmployeeName(employeeDto);
  }

  public getCustomerName(customer?: EnterpriseCustomerV2Dto | EnterpriseCustomerIdDto): string {
    return NameFormattingService.getEnterpriseCustomerName(customer);
  }

  public getImplementTypeLabel(item?: ImplementV2Dto | ImplementIdDto) {
    let implementTypeTranslateable: string;
    if (item && item.type1) {
      implementTypeTranslateable = 'IMPLEMENT.TYPE.' + item.type1.toString();
      if (item.type2) {
        implementTypeTranslateable = implementTypeTranslateable + '_' + item.type2.toString();
        if (item.type3) {
          implementTypeTranslateable = implementTypeTranslateable + '_' + item.type3.toString();
        }
      }
    } else {
      implementTypeTranslateable = '';
    }
    return implementTypeTranslateable;
  }

  public translateAccountingIdFromConfig(accountingConfig: AccountingConfigDto): string {
    if (accountingConfig.id) {
      return this.translateAccountingId(accountingConfig.id);
    }
    return '';
  }

  public translateAccountingId(accountingConfigId: string, ddiOnlyAsNumber?: boolean): string {
    if (accountingConfigId) {
      const translation = this.accountingTranslationMap.get(accountingConfigId);
      if (translation) {
        return translation;
      } else {
        let translation = ' - ';
        // translate can identifier if possible
        translation = this.getTranslation('ENUM.CAN_IDENTIFIER.' + accountingConfigId);
        if (translation.startsWith('ENUM.')) {
          translation = accountingConfigId;
        } else {
          this.accountingTranslationMap.set(accountingConfigId, translation);
          return translation;
        }

        // translate event identifier if possible
        translation = this.getTranslation('ENUM.EVENTTYPE.' + accountingConfigId);
        if (translation.startsWith('ENUM.')) {
          translation = accountingConfigId;
        } else {
          this.accountingTranslationMap.set(accountingConfigId, translation);
          return translation;
        }

        // translate ddi if possible
        let ddi = null;
        let elemNum = null;
        if (ddiOnlyAsNumber) {
          ddi = accountingConfigId;
        } else {
          const splt = accountingConfigId.split('_');
          ddi = splt[2];
          elemNum = splt[3];
        }

        translation = this.getTranslation('ISOBUS.DDI.' + ddi);
        if (translation.startsWith('ISOBUS.')) {
          translation = accountingConfigId;
        } else {
          // append ddi number to translation
          if (elemNum) {
            translation = ddi + ' (' + elemNum + ') ' + translation;
          } else {
            translation = ddi + ' ' + translation;
          }
        }

        this.accountingTranslationMap.set(accountingConfigId, translation);
        return translation;
      }
    }
    return '';
  }
}
